import React, { useState, useEffect, useMemo } from "react";
import {
  fuzzyFilter,
  resolveKeywordsFilter,
  resolveKeywordUpdate,
} from "./utils";

import PropTypes from "prop-types";
import _ from "underscore";

import CreateKeywordModal from "./modals/CreateKeywordModal";
import MetadataFilter from "../MetadataFilter";
import KeywordsTable from "./table/KeywordsTable";
import NoKeywordsAlert from "./alerts/NoKeywordsAlert";

import {
  ThemeProvider,
  Container,
  ScopedCssBaseline,
  Button,
} from "@mui/material";
import theme from "../../../material-ui-common/theme";

import RestrictedComponent from "../../../lib/components/form/RestrictedComponent";
import schema from "../../../routes/permissions/schema";

const KeywordListing = (props) => {
  const { data, isResolving, actions, credentials, store, emitSnack } = props;

  const meta = data.meta;
  const clientKeywords = data.clientKeywords || [];

  const [clientKeywordsSlim, setClientKeywordsSlim] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");

  const clientKeywordsFiltered = useMemo(
    () => fuzzyFilter(searchTerm, clientKeywords, "keyword"),
    [searchTerm, clientKeywords]
  );

  const slimKeywordsFiltered = useMemo(
    () => fuzzyFilter(searchTerm, clientKeywordsSlim, "keyword"),
    [searchTerm, clientKeywordsSlim]
  );

  const [renderedKeywords, setRenderedKeywords] = useState([]);

  useEffect(() => {
    if (clientKeywords.length === 0) { return }
    else { setRenderedKeywords(clientKeywords) }
  }, [clientKeywords])

  const [keywordRequestBody, setKeywordRequestBody] = useState({
    page: 0,
    rowsPerPage: 5,
    listEvents: false,
  });

  useEffect(() => {
    (async () => {
      const result = await resolveKeywordsFilter(
        clientKeywords,
        clientKeywordsSlim,
        clientKeywordsFiltered,
        slimKeywordsFiltered,
        actions.getKeywordsByIds,
        keywordRequestBody.rowsPerPage,
        keywordRequestBody.listEvents
      );

      setRenderedKeywords(result);
    })();
  }, [searchTerm]);

  useEffect(() => {
    actions.getKeywordsSlim((err, res) => {
      setClientKeywordsSlim(res || []);
    });
  }, []);

  const getKeywordsSlim = async () => {
    return await new Promise((resolve, rej) => {
      actions.getKeywordsSlim((err, res) => (err ? rej(err) : resolve(res)));
    });
  };

  const updateKeywordState = async (
    newPage = keywordRequestBody.page,
    newRowsPerPage = keywordRequestBody.rowsPerPage,
    listEvents = keywordRequestBody.listEvents,
    type,
    numToDelete
  ) => {
    await resolveKeywordUpdate(
      newPage,
      newRowsPerPage,
      listEvents,
      type,
      numToDelete,
      searchTerm,
      setSearchTerm,
      getKeywordsSlim,
      setClientKeywordsSlim,
      meta,
      keywordRequestBody,
      setKeywordRequestBody,
      renderedKeywords,
      clientKeywords,
      actions,
      store
    );
  };

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const toggleCreateModal = () => setCreateModalOpen(!createModalOpen);

  const handleCreateKeyword = (newKeyword) => {
    actions.add({ ...newKeyword, userID: credentials.userID }, (err, res) => {
      if (err) {
        return emitSnack({
          type: "error",
          message: "Couldn't Create Keyword",
        });
      }

      emitSnack({
        type: "success",
        message: "Keyword Created",
      });

      updateKeywordState(
        keywordRequestBody.page,
        keywordRequestBody.rowsPerPage,
        keywordRequestBody.listEvents,
        "create"
      );
    });

    toggleCreateModal();
  };

  return (
    <ThemeProvider theme={theme}>
      <Container>
        {!clientKeywords.length && !isResolving && (
          <NoKeywordsAlert toggleCreateModal={toggleCreateModal} />
        )}

        <div className="callout clearfix">
          <div className="col-md-8">
            <MetadataFilter
              value={searchTerm}
              noDebounce
              termChanged={(newSearchTerm) => setSearchTerm(newSearchTerm)}
              isTyping={() => {}}
            />
          </div>

          <div className="col-md-4 text-right">
            <RestrictedComponent
              permissionCheck={schema.MANAGE_CLIENT_KEYWORDS.CREATE}
            >
              <Button onClick={toggleCreateModal} variant="contained">
                Create Keyword
              </Button>
            </RestrictedComponent>
          </div>
        </div>

        <ScopedCssBaseline>
          {clientKeywords.length > 0 && (
            <KeywordsTable
              emitSnack={emitSnack}
              actions={actions}
              credentials={credentials}
              keywords={renderedKeywords}
              meta={meta}
              keywordRequestBody={keywordRequestBody}
              setKeywordRequestBody={setKeywordRequestBody}
              updateKeywordState={updateKeywordState}
              slimKeywords={clientKeywordsSlim || []}
            />
          )}

          <CreateKeywordModal
            keywords={clientKeywordsSlim || []}
            handleClose={toggleCreateModal}
            handleSubmit={handleCreateKeyword}
            open={createModalOpen}
          />
        </ScopedCssBaseline>
      </Container>
    </ThemeProvider>
  );
};

KeywordListing.propTypes = {
  data: PropTypes.any.isRequired,
  emitSnack: PropTypes.func.isRequired,
  isResolving: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired,
  credentials: PropTypes.object.isRequired,
  store: PropTypes.func.isRequired,
};

export default KeywordListing;
